// src/components/layout/AppLayout.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import apiClient from '../../api/axios';
import alerts from '../../lib/alerts';

const AppLayout = () => {
  const { user, logout, accessToken, updatePreferredJurisdiccion } = useAuthStore();
  const navigate = useNavigate();

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const displayName = fullName || user?.username || 'Usuario';
  const email = user?.email || '';
  const userLabel = email ? `${displayName} · ${email}` : displayName;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [expSeconds, setExpSeconds] = useState(null);
  useEffect(() => {
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (payload.exp) setExpSeconds(payload.exp);
      } catch (_) {}
    }
  }, [accessToken]);
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = expSeconds ? Math.max(0, Math.floor(expSeconds - now)) : null;
  const remainingStr = remaining !== null ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}` : '---';

  const [jurisdicciones, setJurisdicciones] = useState([]);
  const [selectedJurisdiccionId, setSelectedJurisdiccionId] = useState('');
  const [savingPreferred, setSavingPreferred] = useState(false);

  useEffect(() => {
    const fetchJurisdicciones = async () => {
      try {
        const resp = await apiClient.get('/calendario/jurisdicciones/');
        const list = resp.data.results || resp.data || [];
        setJurisdicciones(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error cargando jurisdicciones', err);
      }
    };
    fetchJurisdicciones();
  }, []);

  useEffect(() => {
    const preferredId = user?.profile?.preferred_jurisdiccion?.id;
    if (preferredId) {
      setSelectedJurisdiccionId(preferredId);
    }
  }, [user]);

  const handlePreferredChange = (e) => {
    const newId = e.target.value;
    setSelectedJurisdiccionId(newId);
  };

  const handlePreferredSave = async () => {
    if (!selectedJurisdiccionId) return;
    setSavingPreferred(true);
    const result = await updatePreferredJurisdiccion(selectedJurisdiccionId);
    if (result?.success) {
      alerts.success('Preferencia guardada', 'La jurisdicción preferida se actualizó correctamente.');
    } else {
      alerts.error('Error al guardar', result?.error || 'No se pudo actualizar la jurisdicción preferida.');
    }
    setSavingPreferred(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/"><h1 className="text-xl font-bold text-blue-600">LexProcess IA</h1></Link>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Estado:</span>
                <select
                  className="text-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={selectedJurisdiccionId}
                  onChange={handlePreferredChange}
                >
                  <option value="" disabled>Selecciona</option>
                  {jurisdicciones.map((j) => (
                    <option key={j.id} value={j.id}>{j.nombre}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handlePreferredSave}
                  disabled={!selectedJurisdiccionId || savingPreferred}
                  className="text-xs px-2 py-1 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                >
                  {savingPreferred ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
              <span className="font-bold text-gray-700">{userLabel}</span>
              {user?.profile?.rol && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">{user.profile.rol}</span>}
              <span className="text-xs text-gray-500 font-mono">Sesión: {remainingStr}</span>
              <button onClick={handleLogout} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm">Cerrar Sesión</button>
            </div>
          </div>
        </div>
      </nav>
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
